// Private types ---------------------------------------------------------------

type SidebarResizeHook = {
  readonly el: HTMLElement
  readonly pushEventTo: (
    target: HTMLElement,
    event: string,
    payload: Readonly<{ id: string; position: number }>
  ) => unknown
  // Cleanup belongs to this hook instance.
  _cleanup?: () => void
}

// Constants -------------------------------------------------------------------

const SNAP_THRESHOLD = 20

// Private functions -----------------------------------------------------------

/** Parse space-separated numeric string into number array */
const parseSnap = (raw: string): readonly number[] =>
  raw.split(/\s+/).map(Number).filter(Boolean)

/** Read layout configuration from data attributes */
const readConfig = (el: HTMLElement) => ({
  min: Number(el.dataset.min) || 60,
  max: Number(el.dataset.max) || 300,
  default: Number(el.dataset.default) || 256,
  snap: parseSnap(el.dataset.snap || '')
})

/** Keep the divider within its configured limits. */
const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

/** Snap to the nearest point only if within threshold, otherwise keep exact width */
const snapToNearest = (
  width: number,
  snapPoints: readonly number[]
): number => {
  let closest = width
  let minDist = Number.POSITIVE_INFINITY

  for (const point of snapPoints) {
    const dist = Math.abs(width - point)
    if (dist < minDist) {
      closest = point
      minDist = dist
    }
  }

  return minDist <= SNAP_THRESHOLD ? closest : width
}

/** Read the target width from the shell, which survives LiveView patches. */
const readWidth = (shell: HTMLElement, fallback: number): number =>
  Number.parseFloat(shell.style.getPropertyValue('--sidebar-width')) || fallback

/** Let one grid track position both the sidebar and the page content. */
const applyWidth = (
  el: HTMLElement,
  shell: HTMLElement,
  width: number,
  minWidth: number
): void => {
  shell.style.setProperty('--sidebar-width', `${Math.round(width)}px`)
  const collapsed = width <= minWidth
  el.toggleAttribute('data-collapsed', collapsed)
  shell.toggleAttribute('data-collapsed', collapsed)
}

// Public hook -----------------------------------------------------------------

export const SidebarResize = {
  /** Connect local sizing controls to the saved-width event. */
  mounted(this: SidebarResizeHook): void {
    const el = this.el
    const shell = el.closest<HTMLElement>('#app-shell')
    const cfg = readConfig(el)
    const divider = el.querySelector<HTMLElement>('[data-part="divider"]')
    if (!(divider && shell)) {
      return
    }

    applyWidth(el, shell, readWidth(shell, cfg.default), cfg.min)

    // Save the width through the SidebarLive that owns this element.
    const persist = (width: number): void => {
      this.pushEventTo(this.el, 'resize-layout', { id: el.id, position: width })
    }

    // --- Drag ---
    let startX = 0
    let startW = 0

    const dragWidth = (e: PointerEvent): number =>
      snapToNearest(
        clamp(startW + e.clientX - startX, cfg.min, cfg.max),
        cfg.snap
      )

    const onMove = (e: PointerEvent): void => {
      applyWidth(el, shell, dragWidth(e), cfg.min)
    }

    // Release pointer listeners and dragging styles together.
    const stopDragging = (): void => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
      document.removeEventListener('pointercancel', onCancel)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      divider.removeAttribute('data-dragging')
    }

    const onUp = (e: PointerEvent): void => {
      const final = dragWidth(e)
      applyWidth(el, shell, final, cfg.min)
      stopDragging()
      persist(final)
    }

    // Keep the visible width when the browser cancels an active drag.
    const onCancel = (): void => {
      const width = readWidth(shell, cfg.default)
      stopDragging()
      persist(width)
    }

    const onDown = (e: PointerEvent): void => {
      e.preventDefault()
      startX = e.clientX
      startW = el.getBoundingClientRect().width
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      divider.toggleAttribute('data-dragging', true)
      applyWidth(el, shell, startW, cfg.min)
      document.addEventListener('pointermove', onMove)
      document.addEventListener('pointerup', onUp)
      document.addEventListener('pointercancel', onCancel)
    }

    divider.addEventListener('pointerdown', onDown)

    // --- Toggle (dispatched from header button) ---
    const onToggle = (): void => {
      const current = readWidth(shell, cfg.default)
      const next = current <= cfg.min + 5 ? cfg.default : cfg.min

      applyWidth(el, shell, next, cfg.min)
      persist(next)
    }

    el.addEventListener('sidebar:toggle', onToggle)

    // --- Cleanup reference ---
    this._cleanup = () => {
      divider.removeEventListener('pointerdown', onDown)
      el.removeEventListener('sidebar:toggle', onToggle)
      if (divider.hasAttribute('data-dragging')) {
        stopDragging()
      }
    }
  },

  /** Release listeners and any active drag before removing the hook. */
  destroyed(this: SidebarResizeHook): void {
    this._cleanup?.()
  }
}
